import { Routes, Route, Navigate } from "react-router-dom";
import { useEffect, lazy, Suspense } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Helmet } from "react-helmet";
import "./index.css";
import socket from "./socket/index.ts";
import { currentUser, updateSocketStatus } from "./redux/slices/authSlice.ts";

// Lazy Loaded Components for Performance Optimization
const Signup = lazy(() => import("./pages/Signup.tsx"));
const Forgot = lazy(() => import("./pages/Forgot.tsx"));
const Login = lazy(() => import("./pages/Signin.tsx"));
const Reset = lazy(() => import("./pages/Reset.tsx"));
const Flash = lazy(() => import("./pages/Flash.tsx"));
const HomePage = lazy(() => import("./components/Home/Home.tsx"));
const Profile = lazy(() => import("./pages/Profile.tsx"));
const Chatroom = lazy(() => import("./pages/Chatroom.tsx"));
const PaymentPage = lazy(() => import("./pages/Payment.tsx"));
const FaqsPage = lazy(() => import("./pages/FaqsPage.tsx"));
const PrivateMessage = lazy(() => import("./pages/PrivateMessage.tsx"));
const DashboardPage = lazy(() => import("./pages/DashboardPage.tsx"));
const PostJob = lazy(() => import("./pages/PostJob.tsx"));
const EditJob = lazy(() => import("./pages/EditJob.tsx"));
const BidsPage = lazy(() => import("./pages/BidsPage.tsx"));
const UsersChatArea = lazy(() => import("./components/ChatRoom/UsersChatArea.tsx"));
const SavedPostsPage = lazy(() => import("./pages/savedPost.tsx"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard.tsx"));
const CheckEmail = lazy(() => import("./pages/CheckEmail.tsx"));

function App() {
  const currentLoggedInUser = useSelector(currentUser);
  const dispatch = useDispatch();
  const token = useSelector((store) => store.auth.token);
  const userRoleValue = currentLoggedInUser?.role;

  useEffect(() => {
    socket.connect();
    socket.off("CONNECTION_REFRESHED").on("CONNECTION_REFRESHED", () => {
      console.log("Connection refreshed");
      socket.emit("REGISTER_MY_CONNECTION", {
        socketClientID: socket.id,
        id: currentLoggedInUser?._id,
      });
      socket.on("ONLINE", (data) => {
        console.log("Back online", data);
        dispatch(updateSocketStatus("online"));
      });
    });
  }, []);

  const ProtectedRoute = ({ children }) =>
    token ? children : <Navigate to="/login" replace />;

  const AdminProtectedRoute = ({ children }) =>
    token && userRoleValue === "admin" ? children : <Navigate to="/home" replace />;

  return (
    <>
      <Helmet>
        <title>Welcome to JantaNet-Secure Freelancing Platform</title>
        <meta name="description" content="Join our platform to post jobs, bid on projects, and chat securely with clients. Safe and easy job management." />
        <meta name="keywords" content="jobs, freelancers, secure payments, chatroom, online marketplace" />
        <link rel="canonical" href="https://jantanet.com/" />
      </Helmet>

      <div className="w-full h-full">
        <main>
          <Suspense fallback={<div className="text-gray-700 font-semibold">Loading...</div>}>
            <Routes>
              <Route path="/flash" element={<Flash />} />
              <Route path="/forgot" element={<Forgot />} />
              <Route path="/reset/:token" element={<Reset />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Signup />} />
              <Route path="/" element={<Flash />} />
              <Route
                path="/home"
                element={
                  <ProtectedRoute>
                    <HomePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/home/bids"
                element={
                  <ProtectedRoute>
                    <BidsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/jobs/postjob"
                element={
                  <ProtectedRoute>
                    <PostJob />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/post/edit/:jobId"
                element={
                  <ProtectedRoute>
                    <EditJob />
                  </ProtectedRoute>
                }
              />
              <Route path="/profile/:id" element={<Profile />} />
              <Route path="/chatroom" element={<Chatroom />} />
              <Route path="/chatroom/chatarea" element={<UsersChatArea />} />
              <Route path="/home/inbox" element={<PrivateMessage />} />
              <Route path="/payment" element={<PaymentPage />} />
              <Route path="/faqs" element={<FaqsPage />} />
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/saved-posts" element={<SavedPostsPage />} />
              <Route path="/check-email" element={<CheckEmail />} />
              <Route
                path="/admin-dashboard"
                element={
                  <AdminProtectedRoute>
                    <AdminDashboard />
                  </AdminProtectedRoute>
                }
              />
            </Routes>
          </Suspense>
        </main>
      </div>
    </>
  );
}

export default App;
